import React, { useState } from 'react';

import { Alert, Box, Button, Center, Flex, Group, GroupProps, Loader, Stepper, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconLink, IconShirt } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { config } from 'environment/config';
import { ShopifyIcon } from 'icons/ShopifyIcon';
import { SettingsSectionWrapper } from 'pages/Settings/SettingTabs/SettingsSectionWrapper';
import { StoreProductDetails } from 'pages/Settings/SettingTabs/StoresSettingsTab/StoreProductDetails/StoreProductDetails';
import { StoreWebsiteDetails } from 'pages/Settings/SettingTabs/StoresSettingsTab/StoreWebsiteDetails/StoreWebsiteDetails';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { NotificationService } from 'services/notification-service';
import { ResourceService } from 'services/resource-service';
import { ShopifyService } from 'services/shopify-service';
import { RESOURCE_BATCH_UPLOAD_NOTIF_ID } from 'shared/constants/notification-ids';
import {
  RESOURCE_SHOPIFY_STORE_QUERY_KEY,
  SHOPIFY_STORE_PRODUCTS_COUNT_QUERY,
  SHOPIFY_STORE_QUERY_KEY
} from 'shared/constants/query-keys';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import { ResourceAiProcessingStatus, ResourceType } from 'shared/interfaces/Resource';

const TOTAL_STEP_COUNT_INDEX = 1;
const REFETCH_INTERVAL_WHEN_PROCESSING = 2000;
interface ShopifyStoreTileProps {
  storeId: string;
}

export function ShopifyStoreDetails({ storeId }: ShopifyStoreTileProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [subscription] = useSubscriptionContext();
  const [activeStep, setActiveStep] = useState(0);

  const resourceShopifyStoreQuery = useQuery({
    queryFn: () => ResourceService.getResourceByShopifyStoreId(storeId),
    queryKey: [RESOURCE_SHOPIFY_STORE_QUERY_KEY, { shopifyStoreId: storeId }],
    refetchInterval: (resource) => {
      if (resource && resource.aiProcessingStatus == ResourceAiProcessingStatus.IN_PROGRESS) {
        return REFETCH_INTERVAL_WHEN_PROCESSING;
      } else {
        return false;
      }
    }
  });

  const storeQuery = useQuery({
    queryFn: () => ShopifyService.getStoreById(storeId),
    queryKey: [SHOPIFY_STORE_QUERY_KEY, { shopifyStoreId: storeId }],
    onError: () => navigate(ROUTE_LINKS.stores)
  });

  const storeProductsCountQuery = useQuery({
    queryFn: () => ShopifyService.getActiveStoreProductsCount(storeId),
    queryKey: [SHOPIFY_STORE_PRODUCTS_COUNT_QUERY, { shopifyStoreId: storeId }],
    onError: () => navigate(ROUTE_LINKS.stores)
  });

  const configureStoreMutation = useMutation({
    mutationFn: ResourceService.batchUploadResource,
    onSuccess: () => {
      // Keep the notification for 5 second
      setTimeout(() => {
        NotificationService.hideNotification(RESOURCE_BATCH_UPLOAD_NOTIF_ID);
      }, 5000);
      navigate(ROUTE_LINKS.resources);
    },
    onError: (error: AxiosError) => {
      NotificationService.endAction(
        undefined,
        RESOURCE_BATCH_UPLOAD_NOTIF_ID,
        'FEED_BACK.RESOURCES.FAILURE.BATCH_UPLOAD.STORE',
        error
      );
    }
  });

  const onStoreConfigurationConfirm = () => {
    const shopifyStore = storeQuery.data;
    if (!shopifyStore) return;

    configureStoreMutation.mutate({
      resources: [
        {
          fileName: shopifyStore.shop,
          type: ResourceType.SHOPIFY_STORE,
          shopifyStoreId: storeQuery.data._id
        }
      ]
    });
  };

  const nextStep = () => {
    if (configureStoreMutation.isLoading) return;
    setActiveStep((current) => (current < 3 ? current + 1 : current));
  };
  const prevStep = () => {
    if (configureStoreMutation.isLoading) return;
    setActiveStep((current) => (current > 0 ? current - 1 : current));
  };

  if (
    resourceShopifyStoreQuery.isLoading ||
    storeQuery.isLoading ||
    !storeQuery.data ||
    storeProductsCountQuery.isLoading ||
    !storeProductsCountQuery.data
  )
    return <FullScreenLoader />;

  const isStoreConfiguring = !!(
    resourceShopifyStoreQuery.data &&
    resourceShopifyStoreQuery.data.shopifyResourceConfig &&
    resourceShopifyStoreQuery.data.aiProcessingStatus === ResourceAiProcessingStatus.IN_PROGRESS
  );

  const isStoreConfigured = !!(
    resourceShopifyStoreQuery.data &&
    resourceShopifyStoreQuery.data.shopifyResourceConfig &&
    resourceShopifyStoreQuery.data.aiProcessingStatus === ResourceAiProcessingStatus.DONE
  );

  const activeProductCount = storeProductsCountQuery.data;
  const productCountLimit = subscription.product.storeProductsCount;
  const showLimitNotice = activeProductCount > productCountLimit;

  const PreviousButton = () => (
    <Button variant="default" onClick={prevStep}>
      {t('APP.ACTIONS.BACK')}
    </Button>
  );

  const NextButton = () => (
    <Button miw={75} onClick={nextStep}>
      {t('APP.ACTIONS.NEXT')}
    </Button>
  );

  const SyncButton = () => (
    <Button
      miw={75}
      leftIcon={<IconCheck />}
      onClick={onStoreConfigurationConfirm}
      loading={configureStoreMutation.isLoading}>
      {t('APP.ACTIONS.SYNC')}
    </Button>
  );

  const Controls = ({ ...groupProps }: GroupProps) => (
    <Group {...groupProps}>
      {activeStep > 0 && <PreviousButton />}
      {activeStep === TOTAL_STEP_COUNT_INDEX ? <SyncButton /> : <NextButton />}
    </Group>
  );

  const ConfiguredStore = () => (
    <Flex h="100%" direction="column" justify="space-between" style={{ overflow: 'hidden' }}>
      <Flex h="50%" direction="column" pb={10}>
        <Title order={5} mb={5}>
          {t('STORES.PRODUCTS.TITLE')}
        </Title>
        <StoreProductDetails h="100%" style={{ flexGrow: 1 }} storeId={storeId} isStoreConfigured={isStoreConfigured} />
      </Flex>
      <Flex h="50%" direction="column" pt={10}>
        <Title order={5} mb={5}>
          {t('STORES.WEBSITE.TITLE')}
        </Title>
        <StoreWebsiteDetails h="100%" style={{ flexGrow: 1 }} storeId={storeId} isStoreConfigured={isStoreConfigured} />
      </Flex>
    </Flex>
  );

  const StoreIsConfiguring = () => (
    <Center h="100%">
      <Flex direction="column" gap="lg" align="center">
        <Loader size="lg" />
        <Title order={2}>{t('STORES.IS_CONFIGURING', { productCount: activeProductCount })}</Title>
      </Flex>
    </Center>
  );

  const NotConfiguredStore = () => (
    <Box w="100%" h="100%" style={{ position: 'relative', overflow: 'hidden' }}>
      <Stepper
        active={activeStep}
        onStepClick={setActiveStep}
        w="100%"
        h="100%"
        size="md"
        styles={{
          root: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          },
          steps: {
            maxWidth: 550,
            margin: '0 auto',
            paddingTop: 5,
            paddingBottom: 10,
            flexGrow: 0,
            alignItems: 'flex-start',
            '& > button': {
              alignItems: 'flex-start'
            }
          },
          content: {
            height: '100%',
            padding: 0,
            flexGrow: 1,
            flexShrink: 1,
            overflow: 'hidden'
          }
        }}>
        <Stepper.Step
          label={t(`STORES.PRODUCTS.TITLE${isStoreConfigured ? '' : '_WITH_COUNT'}`, { count: activeProductCount })}
          description={t('STORES.PRODUCTS.DESCRIPTION')}
          icon={<IconShirt />}
          w="100%">
          <StoreProductDetails style={{ flexGrow: 1 }} storeId={storeId} isStoreConfigured={isStoreConfigured} />
        </Stepper.Step>
        <Stepper.Step
          label={t('STORES.WEBSITE.TITLE')}
          description={t('STORES.WEBSITE.DESCRIPTION')}
          icon={<IconLink />}
          w="100%">
          <StoreWebsiteDetails style={{ flexGrow: 1 }} storeId={storeId} isStoreConfigured={isStoreConfigured} />
        </Stepper.Step>
      </Stepper>
      <Controls my={15} mr={15} style={{ position: 'absolute', right: 0, top: 0 }} />
      {showLimitNotice && (
        <Alert p={8} icon={<IconAlertCircle />} color="orange" style={{ position: 'absolute', left: 0, top: 0 }}>
          <Text fz={13} maw={350}>
            {t('STORES.PRODUCTS.PRODUCT_LIMIT_NOTICE', {
              productCountLimit,
              activeProductCount
            })}
            <Text
              span
              inherit
              td="underline"
              ml={5}
              style={{ cursor: 'pointer' }}
              onClick={() => window.open(config.shopifyPricingPlanUrl, '_blank')}>
              {t('APP.ACTIONS.UPGRADE_PLAN')}
            </Text>
          </Text>
        </Alert>
      )}
    </Box>
  );

  return (
    <SettingsSectionWrapper
      h="100%"
      icon={
        <Box ml={isStoreConfigured ? undefined : 'md'}>
          <ShopifyIcon w={28} h={28} />
        </Box>
      }
      title={isStoreConfigured ? storeQuery.data.shop : t('STORES.CONFIGURE_TITLE', { store: storeQuery.data.shop })}>
      {isStoreConfiguring ? <StoreIsConfiguring /> : isStoreConfigured ? <ConfiguredStore /> : <NotConfiguredStore />}
    </SettingsSectionWrapper>
  );
}
